import { createSelector } from "@reduxjs/toolkit";
import { buildDashboardData, parseGraphDataForRecharts, computeNonEsVacuumLevelDomain, } from "./graph-data-transforms";
import { ATM_TOLERANCE, USE_LOCAL_GRAPH_DATA, } from "ui/components/panels/graphs/constants";
import { findCupReleaseIndex, getGoverningPump, getSuctionCups, getVacuumPumps, } from "ui/components/panels/graphs/common/helper-functions";
import { mockPerformanceGraphData } from "ui/components/panels/graphs/TEST-DATA/local-test-data";
export var selectPerformanceRoot = function (state) {
    return state.systemPerformance;
};
export var selectPerformanceData = function (state) {
    return selectPerformanceRoot(state).data;
};
export var selectPreloadedPerformance = function (state) {
    return selectPerformanceRoot(state).preloadedData;
};
export var selectPerformanceSystemDataSnapshot = function (state) {
    return selectPerformanceRoot(state).systemDataSnapshot;
};
export var selectIsPerformanceLoading = function (state) {
    return selectPerformanceRoot(state).isLoading;
};
export var selectPerformanceError = function (state) {
    return selectPerformanceRoot(state).error;
};
export var selectIsPerformanceDataValid = function (state) {
    return selectPerformanceRoot(state).isPerformanceDataValid;
};
export var selectHasSettledUserCalculation = function (state) {
    return selectPerformanceRoot(state).hasSettledUserCalculation;
};
export var selectShowEnergySave = function (state) {
    return selectPerformanceRoot(state).showEnergySavePerformance;
};
export var selectPlayLocationPercent = function (state) {
    return selectPerformanceRoot(state).playLocationPercent;
};
export var selectSanityCheckData = function (state) {
    return selectPerformanceRoot(state).sanityCheckData;
};
export var selectIsPerformanceDataPending = function (state) {
    return selectPerformanceRoot(state).isPerformanceDataPending;
};
export var selectCupsOutsideOfObject = function (state) {
    return selectPerformanceRoot(state).cupsOutsideOfObject;
};
export var selectIsEnergySaveAvailableForSystem = createSelector(selectPerformanceData, function (performanceData) {
    return (performanceData === null || performanceData === void 0 ? void 0 : performanceData.energySavingOn) !== undefined;
});
export var selectHasReachedEnergySave = createSelector(selectPerformanceData, function (performanceData) {
    return ((performanceData === null || performanceData === void 0 ? void 0 : performanceData.energySavingOn) &&
        performanceData.energySavingOn.systemTimeToEnergySave > 0);
});
export var selectActivePerformanceData = createSelector(selectPerformanceData, selectShowEnergySave, function (performanceData, showEnergySave) {
    if (!performanceData)
        return;
    if (showEnergySave && performanceData.energySavingOn) {
        return performanceData.energySavingOn;
    }
    else if (!showEnergySave) {
        return performanceData.energySavingOff;
    }
    else {
        return undefined;
    }
});
export var selectIsPostCalculation = createSelector(selectIsPerformanceDataValid, function (isPerformanceDataValid) {
    if (isPerformanceDataValid) {
        return true;
    }
    else {
        return false;
    }
});
export var selectHasReachedPartSecured = createSelector(selectActivePerformanceData, function (activePerformanceData) {
    return activePerformanceData
        ? activePerformanceData.systemTimeToPartSecured > 0
        : false;
});
export var selectVacuumPumpPerformance = function (nodeId) {
    return createSelector(selectActivePerformanceData, function (activePerformanceData) { var _a; return (_a = activePerformanceData === null || activePerformanceData === void 0 ? void 0 : activePerformanceData.nodeData[nodeId]) === null || _a === void 0 ? void 0 : _a.vacuumPump; });
};
export var selectSuctionCupPerformance = function (nodeId) {
    return createSelector(selectActivePerformanceData, function (activePerformanceData) { var _a; return (_a = activePerformanceData === null || activePerformanceData === void 0 ? void 0 : activePerformanceData.nodeData[nodeId]) === null || _a === void 0 ? void 0 : _a.suctionCup; });
};
export var selectDashboardData = createSelector(selectActivePerformanceData, selectPerformanceSystemDataSnapshot, selectHasReachedPartSecured, function (activePerformanceData, systemDataSnapshot, hasReachedPartSecured) {
    if (!activePerformanceData || !systemDataSnapshot)
        return;
    if (!hasReachedPartSecured)
        return;
    return buildDashboardData(activePerformanceData, systemDataSnapshot);
});
export var selectGraphData = createSelector(selectActivePerformanceData, selectSanityCheckData, selectShowEnergySave, function (performanceData, sanityCheckData, showEnergySave) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    var nodeData = (_a = performanceData === null || performanceData === void 0 ? void 0 : performanceData.nodeData) !== null && _a !== void 0 ? _a : {};
    var psIndex = (_b = performanceData === null || performanceData === void 0 ? void 0 : performanceData.psIndex) !== null && _b !== void 0 ? _b : 0;
    var esIndex = (_c = performanceData === null || performanceData === void 0 ? void 0 : performanceData.esIndex) !== null && _c !== void 0 ? _c : 0;
    var placeStopIndex = (_d = performanceData === null || performanceData === void 0 ? void 0 : performanceData.placeStopIndex) !== null && _d !== void 0 ? _d : 0;
    var placeStartIndex = (_e = performanceData === null || performanceData === void 0 ? void 0 : performanceData.placeStartIndex) !== null && _e !== void 0 ? _e : 0;
    // placeStopIndex (the API place_stop) marks place_start + blow_off_time_to_atm,
    // which lands while the cups still hold vacuum; derive the real release
    // point from the cup curves instead, falling back to placeStopIndex.
    var cupReleaseIndex = findCupReleaseIndex(getSuctionCups(nodeData), placeStartIndex, placeStopIndex, ATM_TOLERANCE);
    var timeToEnergySave = performanceData && performanceData.systemTimeToEnergySave > 0
        ? performanceData.systemTimeToEnergySave
        : undefined;
    return USE_LOCAL_GRAPH_DATA
        ? mockPerformanceGraphData
        : {
            systemProperty: {
                timeData: (_f = performanceData === null || performanceData === void 0 ? void 0 : performanceData.vacuumLevelTimeData) !== null && _f !== void 0 ? _f : [],
                airConsumptionVsTime: (_g = performanceData === null || performanceData === void 0 ? void 0 : performanceData.airConsumptionVsTime) !== null && _g !== void 0 ? _g : [],
                robotMotionVsTime: (_h = performanceData === null || performanceData === void 0 ? void 0 : performanceData.robotMotionVsTime) !== null && _h !== void 0 ? _h : [],
                psIndex: psIndex,
                esIndex: esIndex,
                cupReleaseIndex: cupReleaseIndex,
                showEs: showEnergySave && timeToEnergySave,
                placeStartIndex: placeStartIndex,
                airConsumptionTotal: (_j = performanceData === null || performanceData === void 0 ? void 0 : performanceData.airConsumptionTotal) !== null && _j !== void 0 ? _j : 0,
                liftingForceHorizontalVsTime: (_k = performanceData === null || performanceData === void 0 ? void 0 : performanceData.liftingForceHorizontalVsTime) !== null && _k !== void 0 ? _k : [],
                liftingForceVerticalVsTime: (_l = performanceData === null || performanceData === void 0 ? void 0 : performanceData.liftingForceVerticalVsTime) !== null && _l !== void 0 ? _l : [],
            },
            nodeData: nodeData,
            governingPump: getGoverningPump(nodeData, (_m = sanityCheckData === null || sanityCheckData === void 0 ? void 0 : sanityCheckData.governingPumpId) !== null && _m !== void 0 ? _m : 0),
            vacuumPumps: getVacuumPumps(nodeData),
            suctionCups: getSuctionCups(nodeData),
        };
});
/**
 * The Recharts x-axis time (seconds) at which the cups reach atmospheric — the
 * physically-accurate "ATM" moment, derived from the cup vacuum curves
 * (`cupReleaseIndex`) and the same point the graph's vertical ATM reference
 * line is drawn at. Undefined until graph data with a resolved release index is
 * available.
 *
 * This is the single source of truth for ATM: the timeline/SVG ATM marker and
 * the play-head ATM snap target both derive from it (via `displayTimeToPercent`)
 * so the SVG ATM line, the graph ATM reference line and the PlayLine all
 * coincide — including when zoomed in, where any drift between this and the
 * timeline's release-end boundary would otherwise be magnified.
 */
export var selectAtmReleaseTime = createSelector(selectGraphData, function (graphData) {
    var _a = graphData.systemProperty, timeData = _a.timeData, cupReleaseIndex = _a.cupReleaseIndex;
    if (cupReleaseIndex === undefined || cupReleaseIndex <= 0)
        return undefined;
    var time = timeData[cupReleaseIndex];
    return typeof time === "number" ? time : undefined;
});
// We need to parse the pure graph data from the calculation API to fit the expected data structure used by Recharts.
export var selectParsedGraphData = createSelector(selectGraphData, parseGraphDataForRecharts);
/**
 * Returns the vacuum level domain derived from the non-energy-saving data,
 * regardless of which dataset is currently displayed. Used so the cup
 * animation colour always scales against the full (non-ES) vacuum range.
 */
export var selectNonEsVacuumLevelDomain = createSelector(selectPerformanceData, function (performanceData) {
    if (!performanceData)
        return undefined;
    var nonEsData = performanceData.energySavingOff;
    var nodeData = nonEsData.nodeData;
    if (!nodeData)
        return undefined;
    return computeNonEsVacuumLevelDomain(nodeData);
});
